import { ConfirmationService } from 'primeng/api';
import { Observable, lastValueFrom } from 'rxjs';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

export const MILLENNIUM_REEXPORT_CONFIRM_MESSAGE =
  'Limpar data de exportação e reexportar? A composição volta para a fila do robô.';

export const MILLENNIUM_FOUND_NOTICE =
  'A alocação existe no Millennium. A data de exportação permanece e não há reexportação.';

export const MILLENNIUM_REEXPORT_DIALOG_KEY = 'millenniumReexport';

export const MILLENNIUM_LOOKUP_ROLES = [
  'ROLE_ADMIN',
  'ROLE_ALLOC_ADMIN',
  'ROLE_ALLOC_USER'
] as const;

export interface MillenniumAllocation {
  pedidov?: string | number | null;
  status?: string | number | null;
  statusCode?: string | number | null;
  syncedAt?: string | number | null;
  customerOrderNumber?: string | number | null;
  invoiceNumber?: string | number | null;
  shippingOrderId?: string | number | null;
}

export interface MillenniumLookupResult {
  outcome?: string;
  message?: string;
  canReexport?: boolean;
  allocation?: MillenniumAllocation | null;
}

export interface MillenniumReexportResult {
  outcome?: string;
  message?: string;
  clearedBy?: string | null;
  previousDateSyncERP?: string | number | null;
}

export interface MillenniumAllocationRow {
  label: string;
  value: string;
}

export interface MillenniumHttpFailure {
  status?: number;
  message: string;
  keepsDateSyncERP: true;
}

export type MillenniumLookupStep =
  | {
      type: 'show-allocation';
      allocation: MillenniumAllocation;
      message?: string;
    }
  | { type: 'confirm-reexport'; message: string }
  | { type: 'show-message'; message: string };

export type MillenniumLookupRunResult =
  | {
      status: 'cleared';
      clearedBy?: string;
      previousDateSyncERP?: string;
    }
  | { status: 'unchanged' };

export interface CompositionMillenniumClient {
  lookup(compositionId: number): Observable<MillenniumLookupResult>;
  reexport(compositionId: number): Observable<MillenniumReexportResult>;
}

export interface MillenniumLookupPorts {
  lookup: (compositionId: number) => Promise<MillenniumLookupResult>;
  reexport: (compositionId: number) => Promise<MillenniumReexportResult>;
  confirmReexport: () => Promise<boolean>;
  showAllocation: (allocation: MillenniumAllocation, message: string) => void;
  showMessage: (message: string) => void;
  showError: (error: unknown) => void;
}

const DATE_KEPT_SENTENCE = 'A data de exportação não foi alterada.';

export function isValidCompositionId(
  compositionId: unknown
): compositionId is number {
  return (
    typeof compositionId === 'number' &&
    Number.isInteger(compositionId) &&
    compositionId > 0
  );
}

export function compositionHasDateSyncERP(
  composition: { dateSyncERP?: unknown } | null | undefined
): boolean {
  if (!composition) {
    return false;
  }
  const value = composition.dateSyncERP;
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0;
  }
  return false;
}

export function shouldStartMillenniumLookup(input: {
  busy: boolean;
  hasRole: boolean;
  composition: { dateSyncERP?: unknown } | null | undefined;
}): boolean {
  return (
    !input.busy &&
    input.hasRole &&
    compositionHasDateSyncERP(input.composition)
  );
}

export function nextStepForLookup(
  result: MillenniumLookupResult | null | undefined
): MillenniumLookupStep {
  if (result?.outcome === 'FOUND') {
    return {
      type: 'show-allocation',
      allocation: result.allocation ?? {},
      message: optionalText(result.message)
    };
  }
  if (result?.outcome === 'NOT_FOUND' && result.canReexport === true) {
    return {
      type: 'confirm-reexport',
      message: MILLENNIUM_REEXPORT_CONFIRM_MESSAGE
    };
  }
  return {
    type: 'show-message',
    message: terminalLookupMessage(result?.outcome, result?.message)
  };
}

export function nextStepForReexport(
  result: MillenniumReexportResult | null | undefined
):
  | {
      type: 'cleared';
      clearedBy?: string;
      previousDateSyncERP?: string;
    }
  | { type: 'show-message'; message: string } {
  if (result?.outcome === 'CLEARED') {
    return {
      type: 'cleared',
      clearedBy: optionalText(result.clearedBy),
      previousDateSyncERP: optionalText(result.previousDateSyncERP)
    };
  }
  return {
    type: 'show-message',
    message:
      optionalText(result?.message) ||
      `A reexportação não foi concluída. ${DATE_KEPT_SENTENCE}`
  };
}

export function millenniumHttpFailure(error: unknown): MillenniumHttpFailure {
  const status = readStatus(error);
  const body = readErrorBody(error);
  const code = readCode(body) || readCode(error);
  const apiMessage = readBodyMessage(body) || messageWhenNotHttp(error);
  const erpUnavailable = status === 502 || code === 'ERP_INDISPONIVEL';
  if (erpUnavailable) {
    return {
      status: status ?? 502,
      keepsDateSyncERP: true,
      message: withDateKept(
        apiMessage ||
          'Millennium indisponível (ERP_INDISPONIVEL).'
      )
    };
  }
  if (status === 409) {
    return {
      status,
      keepsDateSyncERP: true,
      message: withDateKept(
        apiMessage || 'A composição não pode ser reexportada.'
      )
    };
  }
  if (status === 403) {
    return {
      status,
      keepsDateSyncERP: true,
      message:
        apiMessage || 'Sem permissão para verificar a composição no Millennium.'
    };
  }
  if (status === 400) {
    return {
      status,
      keepsDateSyncERP: true,
      message: withDateKept(
        apiMessage || 'A reexportação foi recusada.'
      )
    };
  }
  return {
    status,
    keepsDateSyncERP: true,
    message: withDateKept(
      apiMessage || 'Erro ao consultar o Millennium.'
    )
  };
}

export function formatMillenniumValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : '—';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não';
  }
  return String(value);
}

export function formatMillenniumSyncedAt(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return formatMillenniumValue(value);
  }
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).format(date);
}

export function millenniumAllocationRows(
  allocation: MillenniumAllocation | null | undefined
): MillenniumAllocationRow[] {
  const source = allocation ?? {};
  return [
    {
      label: 'Pedido Millennium (pedidov)',
      value: formatMillenniumValue(source.pedidov)
    },
    { label: 'Status', value: formatMillenniumValue(source.status) },
    {
      label: 'Código do status',
      value: formatMillenniumValue(source.statusCode)
    },
    {
      label: 'Sincronizado em',
      value: formatMillenniumSyncedAt(source.syncedAt)
    },
    {
      label: 'Pedido do cliente',
      value: formatMillenniumValue(source.customerOrderNumber)
    },
    {
      label: 'Número da nota',
      value: formatMillenniumValue(source.invoiceNumber)
    },
    {
      label: 'Shipping order',
      value: formatMillenniumValue(source.shippingOrderId)
    }
  ];
}

export function infoDialogMessage(message: string): string {
  return escapeDialogHtml(message).replace(/\n/g, '<br>');
}

export function millenniumClearedDetail(result: {
  clearedBy?: string;
  previousDateSyncERP?: string;
}): string {
  const who = result.clearedBy ? ` por ${result.clearedBy}` : '';
  const previous = result.previousDateSyncERP
    ? ` Data anterior: ${formatMillenniumSyncedAt(result.previousDateSyncERP)}.`
    : '';
  return `Data de exportação limpa${who}. A composição voltou para a fila do robô.${previous}`;
}

export function millenniumClearedToast(result: {
  clearedBy?: string;
  previousDateSyncERP?: string;
}): { severity: 'success'; summary: string; detail: string } {
  return {
    severity: 'success',
    summary: 'Sucesso',
    detail: millenniumClearedDetail(result)
  };
}

export function confirmMillenniumReexport(
  confirmationService: ConfirmationService
): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (accepted: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(accepted);
    };
    confirmationService.confirm({
      key: MILLENNIUM_REEXPORT_DIALOG_KEY,
      header: 'Reexportar composição',
      message: MILLENNIUM_REEXPORT_CONFIRM_MESSAGE,
      acceptLabel: 'Limpar e reexportar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => finish(true),
      reject: () => finish(false)
    });
  });
}

export async function runCompositionMillenniumLookup(
  compositionId: number | null | undefined,
  ports: MillenniumLookupPorts
): Promise<MillenniumLookupRunResult> {
  if (!isValidCompositionId(compositionId)) {
    ports.showError({
      message: 'Composição inválida.',
      error: { message: 'Composição inválida.' }
    });
    return { status: 'unchanged' };
  }

  let lookup: MillenniumLookupResult;
  try {
    lookup = await ports.lookup(compositionId);
  } catch (error) {
    ports.showError(error);
    return { status: 'unchanged' };
  }

  const step = nextStepForLookup(lookup);
  if (step.type === 'show-allocation') {
    ports.showAllocation(step.allocation, step.message ?? '');
    return { status: 'unchanged' };
  }
  if (step.type === 'show-message') {
    ports.showMessage(step.message);
    return { status: 'unchanged' };
  }

  let accepted = false;
  try {
    accepted = await ports.confirmReexport();
  } catch (error) {
    ports.showError(error);
    return { status: 'unchanged' };
  }
  if (!accepted) {
    return { status: 'unchanged' };
  }

  let reexportResult: MillenniumReexportResult;
  try {
    reexportResult = await ports.reexport(compositionId);
  } catch (error) {
    ports.showError(error);
    return { status: 'unchanged' };
  }

  const afterPost = nextStepForReexport(reexportResult);
  if (afterPost.type === 'show-message') {
    ports.showMessage(afterPost.message);
    return { status: 'unchanged' };
  }
  return {
    status: 'cleared',
    clearedBy: afterPost.clearedBy,
    previousDateSyncERP: afterPost.previousDateSyncERP
  };
}

export function millenniumLookupPorts(
  service: CompositionMillenniumClient,
  confirmationService: ConfirmationService,
  showAllocation: (allocation: MillenniumAllocation, message: string) => void
): MillenniumLookupPorts {
  const releaseLoader = () => LoaderService.showLoader(false);
  return {
    lookup: (compositionId) =>
      lastValueFrom(service.lookup(compositionId)),
    reexport: (compositionId) =>
      lastValueFrom(service.reexport(compositionId)),
    confirmReexport: async () => {
      releaseLoader();
      const accepted = await confirmMillenniumReexport(confirmationService);
      if (accepted) {
        LoaderService.showLoader();
      }
      return accepted;
    },
    showAllocation: (allocation, message) => {
      releaseLoader();
      showAllocation(allocation, message);
    },
    showMessage: (message) => {
      releaseLoader();
      AppDialogService.showInfoDialog({
        header: 'Millennium',
        message: infoDialogMessage(message)
      });
    },
    showError: (error) => {
      releaseLoader();
      const failure = millenniumHttpFailure(error);
      AppDialogService.showErrorDialog({
        status: failure.status,
        message: failure.message,
        error: { message: failure.message }
      });
    }
  };
}

function terminalLookupMessage(
  outcome: string | undefined,
  message: unknown
): string {
  const text = optionalText(message);
  if (text) {
    return text;
  }
  if (outcome === 'NOT_FOUND') {
    return 'A alocação não foi encontrada no Millennium e a reexportação não foi liberada.';
  }
  return 'Não foi possível confirmar a alocação no Millennium.';
}

function optionalText(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

function withDateKept(message: string): string {
  if (message.includes('data de exportação')) {
    return message;
  }
  const trimmed = message.trim().replace(/[.!?]+\s*$/, '');
  return `${trimmed}. ${DATE_KEPT_SENTENCE}`;
}

function readStatus(error: unknown): number | undefined {
  const record = asRecord(error);
  const status = record?.['status'];
  return typeof status === 'number' ? status : undefined;
}

function readErrorBody(error: unknown): unknown {
  return asRecord(error)?.['error'];
}

function messageWhenNotHttp(error: unknown): string | undefined {
  if (typeof readStatus(error) === 'number') {
    return undefined;
  }
  return readBodyMessage(error);
}

function readBodyMessage(value: unknown): string | undefined {
  const direct = optionalText(value);
  if (direct) {
    return direct;
  }
  const record = asRecord(value);
  if (!record) {
    return undefined;
  }
  const message = optionalText(record['message']);
  if (message && !isHttpClientFailureText(message)) {
    return message;
  }
  const nested = readBodyMessage(record['error']);
  if (nested) {
    return nested;
  }
  return readBodyMessage(record['result']);
}

function isHttpClientFailureText(message: string): boolean {
  return message.startsWith('Http failure response');
}

function readCode(value: unknown): string | undefined {
  if (typeof value === 'string' && value.includes('ERP_INDISPONIVEL')) {
    return 'ERP_INDISPONIVEL';
  }
  const record = asRecord(value);
  if (!record) {
    return undefined;
  }
  for (const key of ['code', 'error', 'errorCode']) {
    const code = optionalText(record[key]);
    if (code === 'ERP_INDISPONIVEL') {
      return code;
    }
  }
  return readCode(record['error']) || readCode(record['result']);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  return value as Record<string, unknown>;
}

function escapeDialogHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
